import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { User, LogOut } from 'lucide-react';
import { useAppContext } from '../../context/AppContextCompat';
import MessageNotifications from '../notifications/MessageNotifications';
import { getRoleLogoPath } from '../../utils/roleHomeRoutes';
import { getHeaderNavItemsForDisplay } from '../../config/mobileNavigation';
import { isNativeUi } from '../../utils/nativeUi';
import WebPlayStoreHeaderLink from '../marketing/WebPlayStoreHeaderLink';

const Header: React.FC = () => {
  const nativeApp = isNativeUi();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, logout } = useAppContext();

  const handleLogout = async () => {
    logout();
    navigate('/auth?mode=login', { replace: true });
  };

  const logoPath = getRoleLogoPath(currentUser?.userType);
  const desktopNavItems = getHeaderNavItemsForDisplay(currentUser?.userType);

  return (
    <header
      className={`fixnow-app-header bg-white shadow-sm border-b border-gray-200 relative z-[100] ${
        nativeApp ? '' : 'pt-[max(0px,env(safe-area-inset-top,0px))]'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={`flex items-center min-h-16 ${
            nativeApp ? 'justify-end' : 'justify-between'
          }`}
        >
          {/* Logo（原生 App 不顯示，與手機版網頁頂欄一致） */}
          {!nativeApp && (
            <Link to={logoPath} className="flex items-center shrink-0 gap-2">
              <img
                src="/brand-logo.png?v=11"
                alt="techfix"
                className="h-8 w-auto max-w-[180px] object-contain"
              />
            </Link>
          )}

          {/* 桌面導覽（手機改由底欄 + 我的／更多頁） */}
          <nav className="hidden md:flex space-x-8">
            {desktopNavItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    item.activePattern.test(location.pathname)
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* 使用者區：通知、頭像、登入／登出 */}
          <div className="flex items-center gap-2 sm:gap-4 relative z-[110] min-w-0">
            <WebPlayStoreHeaderLink />
            {currentUser ? (
              <div className="flex items-center gap-2 sm:gap-4 min-w-0">
                <MessageNotifications />

                <div className="flex items-center gap-2 min-w-0 max-md:max-w-[52vw]">
                  {currentUser.profileImage ? (
                    <img
                      src={currentUser.profileImage}
                      alt={currentUser.name}
                      className="w-8 h-8 shrink-0 rounded-full object-cover border border-gray-200"
                    />
                  ) : (
                    <div className="w-8 h-8 shrink-0 bg-gray-200 rounded-full flex items-center justify-center">
                      <User className="h-4 w-4 text-gray-600" />
                    </div>
                  )}
                  <span className="hidden sm:inline text-sm font-medium text-gray-700 truncate max-w-[8rem] md:max-w-none">
                    {currentUser.name}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex shrink-0 items-center gap-1 rounded-md px-2 py-2 text-sm text-gray-700 hover:text-red-600 transition-colors touch-manipulation min-h-[44px] sm:min-h-0 sm:px-1 sm:py-1"
                >
                  <LogOut className="h-4 w-4 shrink-0" />
                  <span className="hidden sm:inline">登出</span>
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-2 sm:space-x-4">
                <Link
                  to="/auth?mode=login"
                  className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors min-h-[44px] inline-flex items-center px-1"
                >
                  登入
                </Link>
                <Link
                  to="/auth?mode=register"
                  className="bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 transition-colors min-h-[44px] inline-flex items-center"
                >
                  註冊
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
